import { defineStore } from 'pinia'
import { useToast } from "vue-toastification";
const toast = useToast()


function displayError(e){
    if(e?.response?.status == 422){
        const firstErrorKey = Object.keys(e.response.data.errors)[0];
        const firstErrorMessage = e.response.data.errors[firstErrorKey][0];
        toast.error(firstErrorMessage)
        return
    }
    if(e?.response?.status == 403){
        toast.error("You don't have permission to perform this action")
        return
    }
    toast.error('An unexpected error occurred, try again later')
}

function findFolderById(currentFolder, targetId){
    if(currentFolder.info.id == targetId){
        return currentFolder
    }

    const folders = []
    for(let i=0; i<currentFolder.content.length; i++){
        if(currentFolder.content[i].type == 'folder'){
            folders.push(currentFolder.content[i])
        }
    }

    for(let i=0; i<folders.length; i++){
        const folder = findFolderById(folders[i], targetId)
        if(folder){
            return folder
        }
    }
}

function findParentOf(currentFolder, targetId, targetType){
    const content = currentFolder.content
    for(let i=0; i<content.length; i++){
        if(content[i].type == targetType && content[i].info.id == targetId){
            return currentFolder
        }
    }

    const folders = []
    for(let i=0; i<currentFolder.content.length; i++){
        if(currentFolder.content[i].type == 'folder'){
            folders.push(currentFolder.content[i])
        }
    }

    for(let i=0; i<folders.length; i++){
        const folder = findParentOf(folders[i], targetId, targetType)
        if(folder){
            return folder
        }
    }
}

export const useDocumentsStore = defineStore('documents', {
    id: 'documents',
    state: () => ({
        'root': {
            type: 'folder',
            info: {id: 'root'},
            content: [],
            level: 0
        },
        currentlySelectedFolderId: 'root',
        attachableId: null,
        attachableType: null,
        canInteractWithTable: false,
        allContent: {
            folders: [],
            documents: []
        },
        isSearch: false,
        searchResults: [],
        flatFolders: [],
        flatDocuments: []
    }),
    getters: {
        getCurrentlySelectedFolderId(state){
            return state.currentlySelectedFolderId
        },
    },
    actions: {
        setAttachableId(attachableId){
            this.attachableId = attachableId
        },
        setAttachableType(attachableType){
            this.attachableType = attachableType
        },
        setCanInteractWithTable(value){
            this.canInteractWithTable = value
        },
        getFolder(folderId) {
            const folder = this.flatFolders[folderId]
            if(folder){
                return folder
            }
            return []
        },
        addFolder(parentFolderId, folder) {
            const parent = this.flatFolders[parentFolderId]
            if(parent) {
                const newFolder = {
                    type: 'folder',
                    info: folder,
                    content: [],
                    level: parent.level + 1
                }
                this.flatFolders[folder.id] = newFolder
                parent.content.push(newFolder)
            }
        },
        addDocument(parentFolderId, document) {
            const parent = this.flatFolders[parentFolderId]
            if(parent){
                document.metadata.virtual_path = this.resolvePathParts(document.folder_id)
                const newDocument = {
                    type: 'document',
                    info: document,
                    level: parent.level + 1
                }
                this.flatDocuments.push(newDocument)
                parent.content.push(newDocument)
            }
        },
        deleteFolder(folderId) {
            const parent = findParentOf(this.root, folderId, 'folder')
            if(parent){
                delete this.flatFolders[folderId]
                const index = parent.content.findIndex((item) => item.info.id == folderId && item.type == 'folder')
                parent.content.splice(index, 1)
                this.setCurrentlySelectedFolderId('root')
            }
        },
        deleteDocument(documentId) {
            const parent = findParentOf(this.root, documentId, 'document')
            if(parent){
                const idx = this.flatDocuments.findIndex((item) => item.info.id == documentId)
                this.flatDocuments.splice(idx, 1)
                const index = parent.content.findIndex((item) => item.info.id == documentId && item.type == 'document')
                parent.content.splice(index, 1)
            }
        },
        setCurrentlySelectedFolderId(folderId) {
            this.currentlySelectedFolderId = folderId
        },
        updateFolderContentSortOrder(folderId, content){
            const folder = findFolderById(this.root, folderId)
            if(folder){
                folder.content = content
            }
        },
        resolvePathParts(folderId, parts=[]){
            if(folderId == null){
                return parts
            }
            const folder = this.flatFolders[folderId]
            return this.resolvePathParts(folder.info.parent_id, [folder.info.id, ...parts])
        },
        buildPath(parts){
            let path = '/'
            for(let i=0; i<parts.length; i++){
                let name = this.flatFolders[parts[i]].info.name
                path += name + '/'
            }
            return path
        },
        buildTreeFromFlatlist(){
            const folders = {'root': this.root}
            for(let i=0; i<this.allContent.folders.length; i++){
                folders[this.allContent.folders[i].id] = {
                    type: 'folder',
                    info: this.allContent.folders[i],
                    content: []
                }
            }

            // add content to folders
            for(let i=0; i<this.allContent.folders.length; i++){
                const folder = folders[this.allContent.folders[i].id]
                const parentFolder = folder.info.parent_id == null ? folders['root'] : folders[folder.info.parent_id]
                folder.level = parentFolder.level + 1
                parentFolder.content.push(folder)
            }

            for(let i=0; i<this.allContent.documents.length; i++){
                const document = this.allContent.documents[i]
                const parentFolder = document.folder_id == null ? folders['root'] : folders[document.folder_id]
                document.level = parentFolder.level + 1
                parentFolder.content.push({
                    type: 'document',
                    info: document
                })
            }

            // sort content inside folders
            for(let folderId in folders){
                const folder = folders[folderId]
                folder.content = folder.content.sort((a, b) => a.info.display_order - b.info.display_order)
            }

            this.flatFolders = folders

            // add virtual path to documents
            for(let i=0; i<this.allContent.documents.length; i++){
                const document = this.allContent.documents[i]
                const folderPathParts = this.resolvePathParts(document.folder_id)
                document.metadata.virtual_path = folderPathParts
            }

            this.flatDocuments = this.allContent.documents.map((document) => {
                return {
                    type: 'document',
                    info: document
                }
            })
        },
        searchDocuments(name){
            this.isSearch = true
            this.searchResults = []
            for(let i=0; i<this.flatDocuments.length; i++){
                if(this.flatDocuments[i].info.metadata.name.toLowerCase().includes(name.toLowerCase())){
                    this.searchResults.push(this.flatDocuments[i].info)
                }
            }
            this.setCurrentlySelectedFolderId('root')
            this.searchResults = this.searchResults
        },
        cancelSearch(){
            this.isSearch = false
            this.searchResults = []
            this.setCurrentlySelectedFolderId('root')
        },

        // ----------------- API CALLS -----------------
        async uploadDocument(file){
            try{
                const formData = new FormData();
                formData.append('file', file);
                formData.append('attachable_id', this.attachableId);
                formData.append('attachable_type', this.attachableType);
                if(this.currentlySelectedFolderId != 'root'){
                    formData.append('folder_id', this.currentlySelectedFolderId);
                }
                else{
                    formData.append('folder_id', null);
                }

                const headers = { 'Content-Type': 'multipart/form-data' };
                const res = await window.axios.post('/document/private', formData, { headers })
                const document = res.data;
                this.addDocument(this.currentlySelectedFolderId, document)
            }
            catch(e){
                displayError(e)
            }
        },
        async createFolder(name){
            try{
                const res = await window.axios.post('/folder', {
                    name, 
                    parent_id: this.currentlySelectedFolderId == 'root' ? null : this.currentlySelectedFolderId,
                    attachable_id: this.attachableId,
                    attachable_type: this.attachableType
                })
                const folder = res.data
                this.addFolder(this.currentlySelectedFolderId, folder)
            }
            catch(e){
                displayError(e)
            }
        },
        async destroyFolder(folderId){
            try{
                await window.axios.delete('/folder', {
                    data: {id: folderId}
                })
                this.deleteFolder(folderId)
            }
            catch(e){
                displayError(e)
            }
        },
        async destroyDocument(documentId){
            try{
                await window.axios.delete(`/document/private/${documentId}`)
                this.deleteDocument(documentId)
            }
            catch(e){
                displayError(e)
            }
        },
        async renameFolder(folderId, name){
            try{
                await window.axios.patch('/folder', {
                    id: folderId,
                    name
                })
                const folder = findFolderById(this.root, folderId)
                folder.info.name = name
            }
            catch(e){
                displayError(e)
            }
        },
        async updateFolderContentSortOrderApi(folderId, content){
            try{
                await window.axios.patch('/folder/order', {
                    folder_id: folderId == 'root' ? null : folderId,
                    content,
                    attachable_id: this.attachableId,
                    attachable_type: this.attachableType
                })
                this.updateFolderContentSortOrder(folderId, content)
            }
            catch(e){
                displayError(e)
            }
        },
        async fetchAllContent(){
            try{
                const res = await window.axios.get(`/content?attachable_id=${this.attachableId}&attachable_type=${this.attachableType}`)
                this.allContent = res.data
                this.buildTreeFromFlatlist()
            }
            catch(e){
                displayError(e)
            }
        }
    },
})